import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiClock, FiMail } from 'react-icons/fi';
import CheckCircleIcon from '../common/icons/CheckCircleIcon';
import UserIcon from '../common/icons/UserIcon';
import TicketIcon from '../common/icons/TicketIcon';
import QRCodeDisplay from './QRCodeDisplay';
import { calculateOrderPricing } from '../../utils/order';

const OrderSummary = ({ selectedTickets, customerData, qrCode, orderId, totalAmount, ticketPrice = 5 }) => {
  const pricing = calculateOrderPricing(selectedTickets.length, ticketPrice);
  const total = typeof totalAmount === 'number' ? totalAmount : pricing.totalAmount;
  const firstName = customerData.firstName ?? customerData.first_name ?? '';
  const lastName = customerData.lastName ?? customerData.last_name ?? '';
  const email = customerData.email ?? customerData.email_address ?? '';
  const customerName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="card backdrop-blur-sm"
    >
      {/* Success Header */}
      <div className="text-center mb-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-16 h-16 bg-success-500 text-slate-950 rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow"
        >
          <FiCheck className="w-8 h-8" />
        </motion.div>
        <h2 className="text-2xl font-bold text-primary-100 mb-2 flex items-center justify-center gap-2">
          <CheckCircleIcon className="text-success-200" aria-hidden="true" />
          <span>Objednávka vytvorená!</span>
        </h2>
        <p className="text-slate-300">
          Váša objednávka #{orderId} bola úspešne vytvorená
        </p>
      </div>

      {/* Customer Info */}
      <div className="bg-slate-800/60 rounded-xl p-4 mb-6 border border-slate-700">
        <h3 className="font-semibold text-primary-100 mb-3 flex items-center gap-2">
          <UserIcon className="text-primary-300" aria-hidden="true" />
          <span>Vaše údaje</span>
        </h3>
        <div className="space-y-2 text-sm text-slate-300">
          <div className="flex justify-between">
            <span>Meno:</span>
            <span className="font-semibold text-slate-100">{customerName || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span>Email:</span>
            <span className="font-semibold text-slate-100">{email || '—'}</span>
          </div>
        </div>
      </div>

      {/* Selected Tickets */}
      <div className="bg-primary-500/15 rounded-xl p-4 mb-6 border border-primary-500/30">
        <h3 className="font-semibold text-primary-100 mb-3 flex items-center gap-2">
          <TicketIcon className="text-primary-300" aria-hidden="true" />
          <span>Vaše lístky</span>
        </h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedTickets.map(ticket => (
            <span 
              key={ticket}
              className="bg-primary-500 text-slate-950 px-3 py-1 rounded-full text-sm font-medium shadow-ticket"
            >
              {ticket}
            </span>
          ))}
        </div>
        <div className="flex justify-between text-lg font-bold text-primary-200">
          <span>{selectedTickets.length} lístkov</span>
          <span>{total}€</span>
        </div>
        {pricing.freeTickets > 0 && (
          <div className="text-sm text-success-200 mt-2">
            Započítané zdarma: {pricing.freeTickets} lístok
            {pricing.freeTickets === 1 ? '' : 'y'}
          </div>
        )}
      </div>

      {/* QR Code */}
      <div className="mb-6">
        <QRCodeDisplay
          qrCode={qrCode}
          customerName={customerName || 'Neznámy zákazník'}
          orderId={orderId}
          total={total}
        />
      </div>

      {/* Email Notification */}
      <div className="bg-success-500/10 rounded-xl p-4 mb-6 border border-success-500/30">
        <div className="flex items-center gap-3 mb-2">
          <FiMail className="w-5 h-5 text-success-200" />
          <h3 className="font-semibold text-success-100">Email odoslaný</h3>
        </div>
        <p className="text-success-100 text-sm">
          QR kód sme vám odoslali na <strong>{email || '—'}</strong>
        </p>
      </div>

      {/* Payment Instructions */}
      <div className="bg-warning-500/10 rounded-xl p-4 border border-warning-500/40">
        <div className="flex items-center gap-3 mb-3">
          <FiClock className="w-5 h-5 text-warning-200" />
          <h3 className="font-semibold text-warning-100">Ďalšie kroky</h3>
        </div>
        <ol className="text-warning-100 text-sm space-y-2">
          <li className="flex items-start gap-2">
            <span className="bg-warning-500 text-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
            <span>Prejdite k organizátorovi na mieste podujatia</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="bg-warning-500 text-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
            <span>Ukážte QR kód z emailu alebo z tejto stránky</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="bg-warning-500 text-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
            <span>Zaplaťte <strong className="text-warning-100">{total}€ v hotovosti</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="bg-warning-500 text-slate-950 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
            <span>Vaše lístky budú aktivované do tomboly</span>
          </li>
        </ol>
      </div>

    </motion.div>
  );
};

export default OrderSummary;
